import { useEffect, useRef, useState } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from 'recharts';
import { TrendingUp, Users, ShoppingCart, Award, ArrowUpRight, ArrowDownRight, Sparkles, Activity } from 'lucide-react';

/* ─── Detailed Monthly Growth Data ──────────────────────── */
const chartData = [
  { month: 'Jan', revenue: 18.2, conversions: 3.1, roas: 2.8, orders: 1240 },
  { month: 'Feb', revenue: 24.5, conversions: 3.4, roas: 3.1, orders: 1580 },
  { month: 'Mar', revenue: 21.0, conversions: 3.2, roas: 2.9, orders: 1420 },
  { month: 'Apr', revenue: 29.8, conversions: 3.8, roas: 3.5, orders: 1950 },
  { month: 'May', revenue: 34.2, conversions: 4.0, roas: 3.8, orders: 2310 },
  { month: 'Jun', revenue: 31.5, conversions: 3.9, roas: 3.6, orders: 2180 },
  { month: 'Jul', revenue: 42.6, conversions: 4.5, roas: 4.1, orders: 2840 },
  { month: 'Aug', revenue: 38.4, conversions: 4.3, roas: 3.9, orders: 2620 },
  { month: 'Sep', revenue: 47.9, conversions: 4.8, roas: 4.4, orders: 3290 },
  { month: 'Oct', revenue: 53.1, conversions: 5.1, roas: 4.7, orders: 3740 },
  { month: 'Nov', revenue: 61.8, conversions: 5.6, roas: 5.1, orders: 4320 },
  { month: 'Dec', revenue: 58.4, conversions: 5.4, roas: 4.9, orders: 4090 },
];

/* ─── KPI data ──────────────────────────────────────────── */
const KPIS = [
  { label: 'Avg. ROAS',           value: '4.2x',   sub: 'Industry avg: 2.1x', change: '+18.4%', up: true,  icon: TrendingUp   },
  { label: 'Conversion Rate',     value: '+42%',   sub: 'Avg. lift in 90 days', change: '+12.5%', up: true,  icon: ShoppingCart },
  { label: 'Revenue Generated',   value: '₹2.4Cr', sub: 'Across client portfolio', change: '+31.2%', up: true,  icon: Award        },
  { label: 'Client Retention',    value: '95.4%',  sub: 'Long-term retainers', change: '+4.2%',  up: true,  icon: Users        },
];

/* ─── High-Contrast Custom Tooltip ───────────────────────── */
function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const data = payload[0].payload;
  return (
    <div style={{
      background: 'rgba(18, 18, 20, 0.95)',
      backdropFilter: 'blur(12px)',
      border: '1px solid rgba(255, 255, 255, 0.18)',
      boxShadow: '0 12px 36px rgba(0, 0, 0, 0.7), 0 0 20px rgba(236, 43, 40, 0.25)',
      borderRadius: '12px',
      padding: '12px 18px',
      fontFamily: 'Montserrat, sans-serif',
      minWidth: '170px',
      zIndex: 100,
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
        paddingBottom: '6px',
        marginBottom: '8px',
      }}>
        <span style={{ color: '#ffffff', fontWeight: 800, fontSize: '12px', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          {label} 2025
        </span>
        <span style={{ color: '#ec2b28', fontWeight: 700, fontSize: '11px' }}>
          Aurelix Scale
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: '#9ca3af', fontSize: '12px', fontWeight: 500 }}>Revenue</span>
          <span style={{ color: '#ffffff', fontWeight: 800, fontSize: '13px' }}>₹{data.revenue} Lakhs</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: '#9ca3af', fontSize: '12px', fontWeight: 500 }}>ROAS</span>
          <span style={{ color: '#fb923c', fontWeight: 800, fontSize: '12px' }}>{data.roas}x</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: '#9ca3af', fontSize: '12px', fontWeight: 500 }}>Conv. Rate</span>
          <span style={{ color: '#60a5fa', fontWeight: 800, fontSize: '12px' }}>{data.conversions}%</span>
        </div>
      </div>
    </div>
  );
}

/* ─── Timeline Scroll Reveal Hook ────────────────────────── */
function useRevealOnScroll(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, visible };
}

function Reveal({ children, delay = 0, style = {}, className = '' }) {
  const { ref, visible } = useRevealOnScroll(0.1);
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0px)' : 'translateY(24px)',
        transition: `opacity 0.65s cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms, transform 0.65s cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/* ─── Animated Counter ──────────────────────────────────── */
function AnimCounter({ to, prefix = '', suffix = '' }) {
  const [val, setVal] = useState(0);
  const triggered = useRef(false);
  const ref = useRef(null);
  const num = parseFloat(String(to).replace(/[^0-9.]/g, ''));

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !triggered.current) {
        triggered.current = true;
        let i = 0; const steps = 50; const dur = 1200;
        const tick = setInterval(() => {
          i++;
          const p = 1 - Math.pow(1 - i / steps, 3);
          setVal(Math.round(num * p * 10) / 10);
          if (i >= steps) { clearInterval(tick); setVal(num); }
        }, dur / steps);
      }
    }, { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [num]);

  return <span ref={ref}>{prefix}{Number.isInteger(num) ? val : val.toFixed(1)}{suffix}</span>;
}

/* ─── Animated Progress Bar ─────────────────────────────── */
function ProgressBar({ value, max = 100 }) {
  const { ref, visible } = useRevealOnScroll(0.2);
  return (
    <div ref={ref} style={{ width: '100%', height: 6, background: 'rgba(255,255,255,0.15)', borderRadius: 99, overflow: 'hidden' }}>
      <div style={{
        height: '100%',
        background: 'linear-gradient(90deg, #ec2b28 0%, #ff524f 100%)',
        borderRadius: 99,
        width: visible ? `${(value / max) * 100}%` : '0%',
        transition: 'width 1.2s cubic-bezier(0.22, 1, 0.36, 1) 0.2s',
        boxShadow: '0 0 14px rgba(236, 43, 40, 0.7)',
      }} />
    </div>
  );
}

/* ─── Main Section ──────────────────────────────────────── */
export default function AurelixStats() {
  const [activeData, setActiveData] = useState(chartData[chartData.length - 1]);

  const handleMouseMove = (state) => {
    if (state && state.activePayload && state.activePayload.length > 0) {
      setActiveData(state.activePayload[0].payload);
    }
  };

  const handleMouseLeave = () => {
    setActiveData(chartData[chartData.length - 1]);
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800;900&display=swap');

        .as-section {
          background: #080808;
          font-family: 'Bricolage Grotesque', sans-serif;
          padding: clamp(60px, 9vh, 120px) clamp(20px, 6vw, 96px);
          border-top: 1px solid rgba(255, 255, 255, 0.08);
          color: #ffffff;
        }

        /* ── Header ── */
        .as-eyebrow {
          font-family: 'Bricolage Grotesque', sans-serif;
          font-size: 0.7rem;
          font-weight: 800;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: #ec2b28;
          display: block;
          text-align: center;
          margin: 0 auto 12px;
        }
        .as-heading {
          font-family: 'Bricolage Grotesque', sans-serif;
          font-size: clamp(2.4rem, 5.5vw, 4.5rem);
          font-weight: 800;
          letter-spacing: -0.035em;
          color: #ffffff;
          line-height: 1.08;
          margin: 0 0 clamp(36px, 5vh, 60px);
          text-align: center;
          text-transform: none;
        }
        .as-heading em {
          font-style: normal;
          color: #ec2b28;
        }

        /* ── Top grid ── */
        .as-top-grid {
          display: grid;
          grid-template-columns: 1fr 340px;
          gap: 16px;
          margin-bottom: 16px;
        }

        /* ── Chart Card ── */
        .as-card {
          background: #121214;
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 24px;
          overflow: hidden;
          transition: border-color 0.25s, box-shadow 0.25s, transform 0.25s;
        }
        .as-card:hover {
          border-color: rgba(236, 43, 40, 0.35);
          box-shadow: 0 16px 48px rgba(0, 0, 0, 0.5), 0 0 24px rgba(236, 43, 40, 0.12);
        }

        .as-chart-card {
          padding: clamp(22px, 3vw, 34px);
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }

        .as-card-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          flex-wrap: wrap;
          gap: 16px;
          margin-bottom: 20px;
        }

        .as-card-label {
          font-size: 0.65rem;
          font-weight: 800;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: #ec2b28;
          margin-bottom: 6px;
        }
        .as-card-title {
          font-size: clamp(1.05rem, 1.8vw, 1.35rem);
          font-weight: 800;
          color: #ffffff;
          margin: 0;
          letter-spacing: -0.02em;
        }

        /* ── Interactive Metrics Starting Row ── */
        .as-metrics-row {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
          margin-bottom: 20px;
        }
        .as-metric-box {
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 14px;
          padding: 12px 16px;
          display: flex;
          flex-direction: column;
          transition: background 0.2s, border-color 0.2s;
        }
        .as-metric-box:hover {
          background: rgba(255, 255, 255, 0.07);
          border-color: rgba(255, 255, 255, 0.18);
        }
        .as-metric-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 4px;
        }
        .as-metric-title {
          font-size: 0.62rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: #9ca3af;
        }
        .as-metric-tag {
          font-size: 0.58rem;
          font-weight: 800;
          padding: 2px 6px;
          border-radius: 4px;
        }
        .as-metric-value {
          font-size: clamp(1.2rem, 2vw, 1.6rem);
          font-weight: 900;
          letter-spacing: -0.03em;
          color: #ffffff;
          line-height: 1.1;
        }
        .as-metric-sub {
          font-size: 0.65rem;
          color: #9ca3af;
          margin-top: 2px;
          font-weight: 500;
        }

        /* ── Side column ── */
        .as-side-col {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        /* Goal card */
        .as-goal-card {
          background: linear-gradient(145deg, #1c0d0d 0%, #111113 100%);
          border-color: rgba(236, 43, 40, 0.28);
          padding: clamp(22px, 2.5vw, 30px);
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }

        /* Insight card */
        .as-insight-card {
          background: #121214;
          padding: clamp(20px, 2.2vw, 26px);
        }
        .as-insight-icon {
          width: 38px;
          height: 38px;
          border-radius: 10px;
          background: rgba(236, 43, 40, 0.15);
          border: 1px solid rgba(236, 43, 40, 0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 14px;
          flex-shrink: 0;
        }

        /* ── KPI Row ── */
        .as-kpi-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
        }
        .as-kpi-card {
          background: #121214;
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 18px;
          padding: clamp(18px, 2.2vw, 26px);
          transition: border-color 0.22s, background 0.22s, transform 0.22s;
          cursor: default;
        }
        .as-kpi-card:hover {
          border-color: rgba(236, 43, 40, 0.4);
          background: rgba(236, 43, 40, 0.05);
          transform: translateY(-4px);
        }
        .as-kpi-label {
          font-size: 0.65rem;
          font-weight: 800;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          color: #d1d5db;
          margin-bottom: 8px;
        }
        .as-kpi-bottom {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          gap: 8px;
          flex-wrap: wrap;
          margin-bottom: 4px;
        }
        .as-kpi-value {
          font-size: clamp(1.5rem, 2.6vw, 2.1rem);
          font-weight: 900;
          letter-spacing: -0.04em;
          color: #ffffff;
          line-height: 1;
        }
        .as-kpi-badge {
          font-size: 0.68rem;
          font-weight: 800;
          padding: 4px 9px;
          border-radius: 6px;
          display: inline-flex;
          align-items: center;
          gap: 3px;
        }
        .as-badge-up {
          color: #34d399;
          background: rgba(52, 211, 153, 0.15);
          border: 1px solid rgba(52, 211, 153, 0.3);
        }
        .as-kpi-sub {
          font-size: 0.72rem;
          font-weight: 500;
          color: #9ca3af;
          margin: 6px 0 0;
        }

        /* ── Recharts clean styling ── */
        .as-section .recharts-cartesian-axis-tick text {
          fill: #9ca3af !important;
          font-size: 11px;
          font-weight: 600;
          font-family: 'Bricolage Grotesque', sans-serif;
        }
        .as-section .recharts-cartesian-grid line {
          stroke: rgba(255, 255, 255, 0.08) !important;
        }

        /* ── Responsive ── */
        @media (max-width: 960px) {
          .as-top-grid { grid-template-columns: 1fr; }
          .as-side-col { flex-direction: row; }
          .as-kpi-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 640px) {
          .as-section { padding-left: 16px; padding-right: 16px; }
          .as-metrics-row { grid-template-columns: 1fr; }
          .as-side-col { flex-direction: column; }
          .as-card-header { flex-direction: column; }
          .as-kpi-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      <section className="as-section">

        {/* Header */}
        <Reveal delay={0}>
          <span className="as-eyebrow">Enterprise Intelligence</span>
          <h2 className="as-heading">
            Numbers that prove<br /><em>the system is scaling.</em>
          </h2>
        </Reveal>

        {/* ── Top grid: Interactive Chart + Side Targets ── */}
        <div className="as-top-grid">

          {/* Area Chart Card */}
          <Reveal delay={80}>
            <div className="as-card as-chart-card" style={{ height: '100%' }}>
              <div className="as-card-header">
                <div>
                  <p className="as-card-label">Revenue Growth Graph</p>
                  <p className="as-card-title">Monthly Recurring & Portfolio Run-Rate</p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#9ca3af', fontSize: '0.72rem', fontWeight: 600 }}>
                  <Activity size={14} color="#ec2b28" />
                  <span>Hover to scrub timeline: <strong>{activeData.month} 2025</strong></span>
                </div>
              </div>

              {/* Starting Row: Conversion Rate & ROAS & Revenue in same row */}
              <div className="as-metrics-row">
                <div className="as-metric-box">
                  <div className="as-metric-top">
                    <span className="as-metric-title">Portfolio Revenue</span>
                    <span className="as-metric-tag" style={{ background: 'rgba(236,43,40,0.15)', color: '#ec2b28' }}>Live</span>
                  </div>
                  <span className="as-metric-value" style={{ color: '#ffffff' }}>₹{activeData.revenue} Lakhs</span>
                  <span className="as-metric-sub">Indexed across {activeData.orders} orders</span>
                </div>

                <div className="as-metric-box">
                  <div className="as-metric-top">
                    <span className="as-metric-title">Blended ROAS</span>
                    <span className="as-metric-tag" style={{ background: 'rgba(251,146,60,0.15)', color: '#fb923c' }}>High</span>
                  </div>
                  <span className="as-metric-value" style={{ color: '#fb923c' }}>{activeData.roas}x</span>
                  <span className="as-metric-sub">Return on ad spend</span>
                </div>

                <div className="as-metric-box">
                  <div className="as-metric-top">
                    <span className="as-metric-title">Conversion Rate</span>
                    <span className="as-metric-tag" style={{ background: 'rgba(96,165,250,0.15)', color: '#60a5fa' }}>Optimized</span>
                  </div>
                  <span className="as-metric-value" style={{ color: '#60a5fa' }}>{activeData.conversions}%</span>
                  <span className="as-metric-sub">Funnel checkout velocity</span>
                </div>
              </div>

              {/* Clean Single Metric Area Chart */}
              <div style={{ width: '100%', height: 230 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={chartData}
                    margin={{ top: 10, right: 10, left: -16, bottom: 0 }}
                    onMouseMove={handleMouseMove}
                    onMouseLeave={handleMouseLeave}
                  >
                    <defs>
                      <linearGradient id="gRevOnly" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor="#ec2b28" stopOpacity={0.45} />
                        <stop offset="95%" stopColor="#ec2b28" stopOpacity={0.0}  />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="month" tickLine={false} axisLine={false} dy={8} />
                    <YAxis tickLine={false} axisLine={false} dx={-4} domain={[0, 70]} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area
                      type="monotone"
                      dataKey="revenue"
                      name="Revenue"
                      stroke="#ec2b28"
                      strokeWidth={3}
                      fill="url(#gRevOnly)"
                      dot={false}
                      activeDot={{ r: 6, fill: '#ec2b28', stroke: '#ffffff', strokeWidth: 2 }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </Reveal>

          {/* Side column */}
          <div className="as-side-col">

            {/* Goal card */}
            <Reveal delay={160} style={{ flex: 1 }}>
              <div className="as-card as-goal-card" style={{ height: '100%' }}>
                <div>
                  <p className="as-card-label">Primary Metric</p>
                  <p className="as-card-title" style={{ margin: '4px 0 0', color: '#ffffff', fontSize: '1.25rem' }}>
                    Revenue Target<br />FY 2025–26
                  </p>
                </div>
                <div style={{ marginTop: 24 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 10 }}>
                    <span style={{ fontSize: 'clamp(2.2rem, 4.5vw, 3rem)', fontWeight: 900, color: '#ffffff', letterSpacing: '-0.04em', lineHeight: 1 }}>
                      <AnimCounter to={84.6} suffix="%" />
                    </span>
                    <span style={{ fontSize: '0.75rem', color: '#d1d5db', fontWeight: 700 }}>
                      Target: 100%
                    </span>
                  </div>
                  <ProgressBar value={84.6} />
                  <p style={{ fontSize: '0.78rem', color: '#9ca3af', marginTop: 12, lineHeight: 1.6, fontWeight: 500 }}>
                    Pacing 14% ahead of schedule with strong compounding Q3/Q4 momentum.
                  </p>
                </div>
              </div>
            </Reveal>

            {/* Insight card */}
            <Reveal delay={240}>
              <div className="as-card as-insight-card">
                <div className="as-insight-icon">
                  <Sparkles size={18} strokeWidth={2} color="#ec2b28" />
                </div>
                <p style={{ fontSize: '0.82rem', color: '#e5e7eb', lineHeight: 1.65, margin: 0, fontWeight: 500 }}>
                  Organic & Retention channels up{' '}
                  <span style={{ color: '#34d399', fontWeight: 800 }}>+34%</span>{' '}
                  this quarter. Cross-channel attribution accounts for{' '}
                  <span style={{ color: '#ffffff', fontWeight: 800 }}>42%</span>{' '}
                  of net-new revenue lift.
                </p>
              </div>
            </Reveal>

          </div>
        </div>

        {/* ── KPI Row ── */}
        <div className="as-kpi-grid">
          {KPIS.map((kpi, i) => {
            const Icon = kpi.icon;
            return (
              <Reveal key={kpi.label} delay={100 + i * 80}>
                <div className="as-kpi-card">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                    <p className="as-kpi-label">{kpi.label}</p>
                    <Icon size={16} strokeWidth={2.2} color="#ec2b28" />
                  </div>
                  <div className="as-kpi-bottom">
                    <span className="as-kpi-value">{kpi.value}</span>
                    <span className={`as-kpi-badge ${kpi.up ? 'as-badge-up' : 'as-badge-down'}`}>
                      {kpi.up
                        ? <><ArrowUpRight size={12} strokeWidth={2.5} /> {kpi.change}</>
                        : <><ArrowDownRight size={12} strokeWidth={2.5} /> {kpi.change}</>
                      }
                    </span>
                  </div>
                  <p className="as-kpi-sub">{kpi.sub}</p>
                </div>
              </Reveal>
            );
          })}
        </div>

      </section>
    </>
  );
}
